/**
 * 题目 HTML 统一处理模块（quiz 渲染专用）
 *
 * ⚠️ 严格所见即所得：本函数只做"无损且必要"的变换，绝不改变用户的排版意图。
 *
 * 图片尺寸存储源：data-edit-w 属性（落地数据库）
 * 渲染时：从 data-edit-w 读取并注入 style="width:N%"
 *
 * 处理顺序（严格固定，不可调整）：
 * 1. 图片 URL 规范化（无损，必要：浏览器需要完整 URL 才能加载图片）
 * 2. width% 注入（无损，必要：否则图片尺寸不生效）
 *    - 优先级 A：img 自身 style 中的 width（用户拖拽时已写入）
 *    - 优先级 B：img 的 data-edit-w 属性（存储源）
 *    - 优先级 C：外部传入的 widths 数组（兼容旧调用方）
 *    - 优先级 D：默认 100%
 *
 * 设计原则：
 * - 用户在编辑器里怎么排，quiz 就怎么显示
 * - 排版意图由数据源（数据库 content 字段）决定，不由本函数决定
 * - 本函数只负责"渲染前的必要清理"，不负责"排版规范化"
 * - 不做空段清理、不做 <br> 压缩、不注入任何 display/margin/flex 样式
 */

import { getSupabaseUrl } from '../supabase/client';

/**
 * 规范化图片 URL（兼容 /sb-api/、/storage/、完整 URL）
 */
function normalizeImageUrl(src: string): string {
  if (!src) return '';
  if (src.startsWith('http://') || src.startsWith('https://')) return src;
  if (src.startsWith('/storage/')) return `${getSupabaseUrl()}${src}`;
  if (src.startsWith('/sb-api/')) return src;
  return src;
}

/**
 * 处理 HTML 中所有 <img> 标签的 src
 * - 移除 src 为空或无 src 的 img 标签（避免破碎图标）
 * - 规范化 src URL
 */
function processAllImageUrls(html: string): string {
  if (!html) return html;
  return html.replace(/<img([^>]*)>/gi, (match, attrs) => {
    const srcMatch = attrs.match(/src="([^"]+)"/i);
    // 删除图片时 Quill 可能残留 <img src=""> 或无 src 的空 img 标签
    // 这些空 img 会被浏览器渲染为破碎图标，必须整体移除
    if (!srcMatch) return '';

    const originalSrc = srcMatch[1];
    const normalizedSrc = normalizeImageUrl(originalSrc);
    const newAttrs = attrs.replace(/src="([^"]+)"/i, `src="${normalizedSrc}"`);
    return `<img${newAttrs}>`;
  });
}

/**
 * 从单个 img 标签的属性中提取宽度百分比
 * 优先级：style width > data-edit-w > undefined
 */
function extractImgWidth(attrs: string): number | undefined {
  // 优先级 A：style 中的 width:N%
  const styleMatch = attrs.match(/style="([^"]*)"/i);
  if (styleMatch) {
    const styleContent = styleMatch[1];
    const widthMatch = styleContent.match(/width:\s*(\d+(?:\.\d+)?)%/i);
    if (widthMatch) {
      const w = parseFloat(widthMatch[1]);
      if (!isNaN(w) && w > 0) return w;
    }
  }

  // 优先级 B：data-edit-w 属性
  const dataWMatch = attrs.match(/data-edit-w="([^"]+)"/i);
  if (dataWMatch) {
    const w = parseFloat(dataWMatch[1]);
    if (!isNaN(w) && w > 0) return w;
  }

  return undefined;
}

/**
 * 统一的题目 HTML 渲染预处理函数
 * 编辑器和 quiz 预览模式共用此函数，确保两端渲染完全一致
 *
 * 严格所见即所得：只做 URL 规范化 + width% 注入，不做任何排版变换。
 *
 * @param html - 原始 HTML
 * @param widths - 图片宽度百分比数组（可选，作为优先级 C 的回退）
 * @returns 处理后的 HTML，可直接用于 dangerouslySetInnerHTML 或 Quill defaultValue
 */
export function processQuestionHtmlForRender(html: string, widths: number[] = []): string {
  if (!html) return html;

  let result = html;

  // ========== 阶段 0：坐标 nowrap（防止坐标在逗号后断行） ==========
  // 将 (数字, 数字) 模式包裹 nowrap，避免容器宽度不足时坐标被拆成两行
  result = result.replace(/(\(-?\d+(?:\.\d+)?,\s*-?\d+(?:\.\d+)?\))/g, '<span style="white-space:nowrap">$1</span>');

  // ========== 阶段 1：图片 URL 规范化（必要，无损） ==========
  result = processAllImageUrls(result);

  // ========== 阶段 2：width% 注入（必要，无损） ==========
  // 优先级：style width > data-edit-w > 外部 widths 数组 > 默认 100%
  // 严格所见即所得：不做空段清理、不做 <br> 压缩、不注入任何 display/margin/flex 样式
  const hasImages = result.includes('<img');
  if (hasImages) {
    let imgIndex = 0;
    result = result.replace(/<img([^>]*?)>/g, (match, attrs) => {
      // 优先从 img 自身属性提取宽度
      const selfWidth = extractImgWidth(attrs);
      // 回退到外部 widths 数组
      const fallbackWidth = imgIndex < widths.length ? widths[imgIndex] : 100;
      const finalWidth = selfWidth ?? fallbackWidth;
      imgIndex++;

      // 同步 data-edit-w 属性（如果 img 自身有宽度但没 data-edit-w，补上）
      let newAttrs = attrs;
      if (selfWidth !== undefined && !/data-edit-w=/i.test(newAttrs)) {
        newAttrs = ` data-edit-w="${selfWidth}"${newAttrs}`;
      }

      // 注入/更新 style 中的 width
      if (/style="/i.test(newAttrs)) {
        return match.replace(/<img([^>]*?)>/, (_m, a: string) => {
          const updatedAttrs = a.replace(/style="([^"]*)"/i, (_sm: string, styleContent: string) => {
            // 移除已有 width:N%
            const cleanedStyle = styleContent.replace(/(?<!max-)width:\s*\d+%;?\s*/g, '');
            return `style="width:${finalWidth}%;${cleanedStyle}"`;
          });
          return `<img${updatedAttrs}>`;
        });
      }
      return `<img${newAttrs} style="width:${finalWidth}%;">`;
    });
  }

  return result;
}

/**
 * 从 HTML 中提取所有图片的 src URL（已规范化）
 * 用于 quiz 预览模式判断是否有图片
 */
export function extractImageUrls(html: string): string[] {
  if (!html) return [];
  const imgRegex = /<img[^>]*src=["']([^"']+)["'][^>]*>/gi;
  const images: string[] = [];
  let match;
  while ((match = imgRegex.exec(html)) !== null) {
    images.push(normalizeImageUrl(match[1]));
  }
  return images;
}

/**
 * 从 HTML 中提取所有图片的宽度百分比数组
 * 优先级：style width > data-edit-w > 默认 100
 *
 * 用于：
 * - questionMapper 从 HTML 同步派生 titleImageWidths（兼容旧代码）
 * - QuillEditorWithImageResize 拖拽完成后从 DOM 提取宽度
 *
 * @param html - 原始 HTML
 * @returns 宽度百分比数组，与 HTML 中 img 顺序一一对应
 */
export function extractImageWidths(html: string): number[] {
  if (!html) return [];
  const widths: number[] = [];
  const imgRegex = /<img([^>]*)>/gi;
  let match;
  while ((match = imgRegex.exec(html)) !== null) {
    const w = extractImgWidth(match[1]);
    widths.push(w ?? 100);
  }
  return widths;
}

/**
 * 将 HTML 中所有 img 的宽度写入 data-edit-w 属性
 * 用于：Quill 编辑器拖拽缩放完成后，把 style 中的 width 同步到 data-edit-w（存储源）
 *
 * @param html - 原始 HTML（img 已有 style="width:N%"）
 * @returns HTML（img 同时有 style 和 data-edit-w）
 */
export function syncWidthsToDataAttr(html: string): string {
  if (!html) return html;
  return html.replace(/<img([^>]*?)>/gi, (match, attrs) => {
    const width = extractImgWidth(attrs);
    if (width === undefined) return match;

    // 已有 data-edit-w：更新
    if (/data-edit-w=/i.test(attrs)) {
      return match.replace(/data-edit-w="[^"]*"/i, `data-edit-w="${width}"`);
    }
    // 新增 data-edit-w
    return `<img${attrs} data-edit-w="${width}">`;
  });
}
